import React from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  Person,
  Notifications,
  Work,
  PowerOff,
  LocalOffer,
  Lock,
  Language,
  Category,
  DocumentScanner,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import useTranslation from "../../../hooks/useTranslation";
import notificationStore from "../../../stores/NotificationStore";
import authStore from "../../../stores/AuthStore";
import { UserRoles } from "../../../stores/AuthStore/types";
import caseStore from "../../../stores/CaseStore";
import configStore from "../../../stores/ConfigStore";
import offerStore from "../../../stores/OfferStore";
import Dropdown from "../../Dropdown";
import { languageOptions } from "../../../languages";
import { DropdownItem } from "../../Dropdown/types";
import { AppLang } from "../../../stores/ConfigStore/types";
import styles from "./DrawerMenu.module.scss";
import ChangePassword from "../ChangePassword";

interface DrawerMenuProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const DrawerMenu = ({ open, setOpen }: DrawerMenuProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [changePasswordOpen, setChangePasswordOpen] = React.useState(false);

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  const handleLogoutClick = () => {
    authStore.logout().then();
  };

  const handleOnLanguageSelected = (item: DropdownItem) => {
    configStore.setCurrentLanguage(item.key as AppLang);
  };

  return (
    <>
      <Drawer open={open} onClose={toggleDrawer(false)}>
        <List onClick={toggleDrawer(false)}>
          <ListItem>
            <ListItemText
              primary={
                <Typography variant="h6">
                  {authStore.userHasRole(UserRoles.LAWYER) && "Av. "}
                  {authStore.user.firstName} {authStore.user.lastName}
                </Typography>
              }
            />
          </ListItem>
          <ListItem onClick={() => navigate("/profile")}>
            <ListItemIcon sx={{ "min-width": "40px" }}>
              <Person />
            </ListItemIcon>
            <ListItemText primary={t("profile")} />
          </ListItem>
          <ListItem onClick={() => setChangePasswordOpen(true)}>
            <ListItemIcon sx={{ "min-width": "40px" }}>
              <Lock />
            </ListItemIcon>
            <ListItemText primary={t("changePassword")} />
          </ListItem>
          <ListItem onClick={() => navigate("/notifications")}>
            <ListItemIcon sx={{ "min-width": "40px" }}>
              <Notifications />
            </ListItemIcon>
            <ListItemText
              primary={t("notifications")}
              secondary={`(${notificationStore.notifications.filter((n) => !n.isRead).length})`}
            />
          </ListItem>
          {authStore.userHasRole(UserRoles.USER) && (
            <ListItem onClick={() => navigate("/cases")}>
              <ListItemIcon sx={{ "min-width": "40px" }}>
                <Work />
              </ListItemIcon>
              <ListItemText
                primary={t("your-cases")}
                secondary={`(${caseStore.userCases.length} / ${configStore.appConfig.caseLimit})`}
              />
            </ListItem>
          )}
          {(authStore.userHasRole(UserRoles.USER) ||
            authStore.userHasRole(UserRoles.LAWYER)) && (
            <ListItem onClick={() => navigate("/incoming-offers")}>
              <ListItemIcon sx={{ "min-width": "40px" }}>
                <LocalOffer />
              </ListItemIcon>
              <ListItemText
                primary={t("incoming-offers")}
                secondary={`(${offerStore.userOffers.length})`}
              />
            </ListItem>
          )}
          {authStore.userHasRole(UserRoles.LAWYER) && (
            <ListItem onClick={() => navigate("/offers")}>
              <ListItemIcon sx={{ "min-width": "40px" }}>
                <LocalOffer />
              </ListItemIcon>
              <ListItemText
                primary={t("your-offers")}
                secondary={`(${offerStore.lawyerOffers.length})`}
              />
            </ListItem>
          )}
          {authStore.userHasRole(UserRoles.ADMIN) && (
            <ListItem onClick={() => navigate("/dictionary-management")}>
              <ListItemIcon sx={{ "min-width": "40px" }}>
                <Language />
              </ListItemIcon>
              <ListItemText primary={"Çeviriler"} />
            </ListItem>
          )}
          {authStore.userHasRole(UserRoles.ADMIN) && (
            <ListItem onClick={() => navigate("/categories-management")}>
              <ListItemIcon sx={{ "min-width": "40px" }}>
                <Category />
              </ListItemIcon>
              <ListItemText primary={"Kategoriler"} />
            </ListItem>
          )}
          {authStore.userHasRole(UserRoles.ADMIN) && (
            <ListItem
              onClick={() =>
                navigate("/static-content-management/user-agreement")
              }
            >
              <ListItemIcon sx={{ "min-width": "40px" }}>
                <DocumentScanner />
              </ListItemIcon>
              <ListItemText primary={"Kategoriler"} />
            </ListItem>
          )}
          <ListItem onClick={handleLogoutClick}>
            <ListItemIcon sx={{ "min-width": "40px" }}>
              <PowerOff />
            </ListItemIcon>
            <ListItemText primary={t("logout")} />
          </ListItem>
        </List>
        <div className={styles.languagePicker}>
          <Dropdown
            activeKey={configStore.currentLanguage}
            options={languageOptions}
            onItemSelected={handleOnLanguageSelected}
            placement="top"
          />
        </div>
      </Drawer>
      <ChangePassword
        dialogOpen={changePasswordOpen}
        setDialogOpen={setChangePasswordOpen}
      />
    </>
  );
};

export default DrawerMenu;
